import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LEARN, stickerButton } from "../../theme";
import { COUNTING_ENGINE_THEME, countingChoices, countingSpokenWord, countingTarget } from "../../../../data/countingLadderCatalog";
import { ConfettiBurst } from "../../feedback/Celebration";
import { CountBadge, CountingPanel, CountingPiece, CountingRoot, NumberCard, PanelChip, POP_SPRING, darkenHex, useCompleteOnce } from "../CountingPiece";

/**
 * Conservation of number: count the tight cluster of stones, watch them
 * tumble apart, then say how many there are now (the same amount).
 */
const RearrangeGame = ({ activity, speak, sfx, onComplete, onWrong, reduceMotion = false }) => {
  const total = countingTarget(activity);
  const accent = COUNTING_ENGINE_THEME.rearrange.accent;
  const [spread, setSpread] = useState(false);
  const [askable, setAskable] = useState(false);
  const [tagged, setTagged] = useState([]);
  const [picked, setPicked] = useState(null);
  const [burst, setBurst] = useState(0);
  const complete = useCompleteOnce(onComplete);
  const solved = picked?.ok === true;

  useEffect(() => {
    if (!spread) return undefined;
    const t = setTimeout(() => setAskable(true), reduceMotion ? 0 : 750);
    return () => clearTimeout(t);
  }, [spread, reduceMotion]);

  const tap = (i) => {
    if (tagged.includes(i) || solved) return;
    const next = [...tagged, i];
    setTagged(next);
    sfx?.("pop");
    speak?.(countingSpokenWord(next.length), { interrupt: true });
  };

  const doSpread = () => {
    sfx?.("whoosh", { volume: 0.5 });
    setSpread(true);
  };

  const choose = (value) => {
    if (solved) return;
    if (value === total) {
      setPicked({ value, ok: true });
      sfx?.("correct");
      setBurst((b) => b + 1);
      complete();
      return;
    }
    setPicked({ value, ok: false });
    onWrong?.();
    setTimeout(() => setPicked((p) => (p?.value === value && !p.ok ? null : p)), 600);
  };

  const pieceSize = "max(56px, calc(var(--tile) * 0.95))";
  const chipText = solved ? "Still the same" : askable ? "How many now?" : spread ? "Watch them tumble" : "Count the pile";

  return (
    <CountingRoot>
      <CountingPanel grow tint={accent} chip={<PanelChip text={chipText} accent={accent} />}>
        <div
          style={{
            display: "flex", flexWrap: "wrap", justifyContent: "center", alignItems: "center", alignContent: "center",
            gap: spread ? "clamp(14px, 4vmin, 34px)" : 0, maxWidth: spread ? "100%" : "calc(var(--tile) * 3.9)",
            transition: reduceMotion ? "none" : "gap 700ms cubic-bezier(0.34, 1.15, 0.64, 1), max-width 700ms cubic-bezier(0.34, 1.15, 0.64, 1)",
          }}
        >
          {Array.from({ length: total }, (_, i) => {
            const position = tagged.indexOf(i);
            return (
              <motion.button
                key={i}
                type="button"
                className="counting-btn"
                layout={!reduceMotion}
                aria-label={position >= 0 ? `Stone ${i + 1}, counted ${position + 1}` : `Stone ${i + 1}`}
                onClick={() => tap(i)}
                initial={reduceMotion ? false : { scale: 0 }}
                animate={{ scale: 1, rotate: spread ? ((i % 3) - 1) * 14 : ((i % 2) - 0.5) * 6 }}
                transition={reduceMotion ? { duration: 0 } : { ...POP_SPRING, delay: spread ? 0 : Math.min(i * 0.05, 0.5) }}
                whileTap={reduceMotion || position >= 0 ? undefined : { scale: 0.92 }}
                whileHover={reduceMotion || position >= 0 || solved ? undefined : { scale: 1.08 }}
                style={{ background: "none", border: "none", padding: 0, margin: spread ? 0 : "-4px", cursor: position >= 0 ? "default" : "pointer", position: "relative", display: "inline-flex", borderRadius: 999, touchAction: "manipulation" }}
              >
                <CountingPiece asset="pebble" variant="bare" size={pieceSize} phase={i} reduceMotion={reduceMotion} ring={position >= 0 ? accent : undefined} lifted={position >= 0} style={{ borderRadius: "50%" }} />
                {position >= 0 && <CountBadge n={position + 1} accent={accent} reduceMotion={reduceMotion} />}
              </motion.button>
            );
          })}
        </div>
        {burst > 0 && <ConfettiBurst key={`burst-${burst}`} count={28} originY={40} reduceMotion={reduceMotion} />}
      </CountingPanel>

      <div style={{ minHeight: "calc(var(--tile) * 1.4)", display: "flex", alignItems: "center", justifyContent: "center", gap: "calc(var(--tile) * 0.16)", flexWrap: "wrap" }}>
        <AnimatePresence mode="wait">
          {!spread ? (
            <motion.button
              key="spread"
              type="button"
              className="counting-btn"
              onClick={doSpread}
              whileTap={reduceMotion ? undefined : { scale: 0.96, y: 3 }}
              exit={{ opacity: 0, scale: 0.9 }}
              style={{ ...stickerButton({ bg: accent, deep: darkenHex(accent), size: "lg", color: LEARN.color.ink }) }}
            >
              Spread the stones
            </motion.button>
          ) : askable ? (
            <motion.div key="choices" initial={reduceMotion ? false : { y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} style={{ display: "flex", gap: "calc(var(--tile) * 0.16)", flexWrap: "wrap", justifyContent: "center" }}>
              {countingChoices(total, activity.maximumNumber).map((value) => (
                <NumberCard
                  key={value}
                  accent={accent}
                  reduceMotion={reduceMotion}
                  disabled={solved}
                  state={picked?.value === value ? (picked.ok ? "correct" : "wrong") : "idle"}
                  onClick={() => choose(value)}
                >
                  {value}
                </NumberCard>
              ))}
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    </CountingRoot>
  );
};

export default RearrangeGame;
